import { useEffect, useRef } from "react";
import { isMac } from "../platform-utils";

/**
 * Add an event handler on the document. If no key is provide, will trigger on all key presses.
 */
export function useKeyDown(
  onKeyDown: () => void,
  {
    key,
    mod,
    shift,
  }: {
    /** Value of event.code. Undefined will fire on all keypresses.
     * [See MDN for possible values](https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values). */
    key?: string;
    mod?: boolean;
    shift?: boolean;
  },
) {
  const keyDownRef = useRef(onKeyDown);
  keyDownRef.current = onKeyDown;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.key === key || !key) &&
        (mod == null || (mod && isModPressed(e)) || (!mod && !isModPressed(e))) &&
        (shift == null || (shift && isShiftPressed(e)) || (!shift && !isShiftPressed(e)))
      ) {
        keyDownRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [key, onKeyDown, mod, shift]);
}

/** Checks if the system's mod key is pressed  */
function isModPressed(e: KeyboardEvent) {
  return isMac() ? e.metaKey : e.ctrlKey;
}

function isShiftPressed(e: KeyboardEvent) {
  return e.shiftKey;
}
